import { supabase } from './supabase';

export interface Profile {
  id: string;
  username: string;
  full_name?: string;
  avatar_url?: string;
  banner_url?: string;
  bio?: string;
  notification_email?: string;
  website?: string;
  instagram?: string;
  x_twitter?: string;
  linkedin?: string;
  youtube?: string;
  created_at: string;
  updated_at: string;
  verified?: boolean;
  video_intro_url?: string;
}

export async function getProfile(userId: string): Promise<Profile | null> {
  try {
    // First try to get existing profile
    const { data, error } = await supabase
      .from('users')
      .select('id, username, full_name, avatar_url, banner_url, bio, notification_email, website, instagram, x_twitter, linkedin, youtube, created_at, updated_at, verified, video_intro_url')
      .eq('id', userId)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    // If profile exists, return it
    if (data) {
      return data;
    }

    // Profile doesn't exist - seed a minimal profile without overwriting
    // any profile that might be created concurrently (e.g. signup RPC).
    const username = `user_${userId.slice(0, 8)}`;

    const { error: seedError } = await supabase
      .from('users')
      .upsert([{
        id: userId,
        username: username,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }], {
        onConflict: 'id',
        // Critical: do not update existing rows (prevents username clobbering).
        ignoreDuplicates: true
      })
      .select();

    if (seedError) {
      console.error('Profile seed failed:', seedError);
      return null;
    }

    const { data: seededProfile, error: seededFetchError } = await supabase
      .from('users')
      .select('id, username, full_name, avatar_url, banner_url, bio, notification_email, website, instagram, x_twitter, linkedin, youtube, created_at, updated_at, verified, video_intro_url')
      .eq('id', userId)
      .maybeSingle();

    if (seededFetchError) throw seededFetchError;
    return seededProfile;
  } catch (error) {
    console.error('Error fetching profile:', error);
    return null;
  }
}

export async function updateProfile(userId: string, updates: Partial<Profile>): Promise<boolean> {
  try {
    // Only allow updating specific fields
    const allowedUpdates = {
      full_name: updates.full_name,
      avatar_url: updates.avatar_url,
      banner_url: updates.banner_url,
      bio: updates.bio,
      notification_email: updates.notification_email,
      website: updates.website,
      instagram: updates.instagram,
      x_twitter: updates.x_twitter,
      linkedin: updates.linkedin,
      youtube: updates.youtube,
      video_intro_url: updates.video_intro_url,
      updated_at: new Date().toISOString()
    };

    // Remove undefined values
    Object.keys(allowedUpdates).forEach(key => {
      if (allowedUpdates[key] === undefined) {
        delete allowedUpdates[key];
      }
    });

    const { error } = await supabase
      .from('users')
      .update(allowedUpdates)
      .eq('id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating profile:', error);
    return false;
  }
}
